import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, ChevronDown, ChevronUp, X, Plus, MapPin } from "lucide-react";
import { MdOutlineLocationOn } from "react-icons/md";

import {
    addBranch,
    updateBranch,
    deleteBranch,
    getPartner,
} from "../../../../Service/partnerAPI";

import { searchPlaces, getPlaceDetails } from "../../../../Service/OlaMapsService";

import Loader from "../../../../reusableUI/loader";
import { showToast } from "../../../../reusableUI/toaster";
import { formatPhoneNumber } from "../../../../utils/Functions";

/* ---------------- PHONE CODES ---------------- */
const phoneCodes = [{ code: "+91", digits: "91", label: "+91" }];

/* ---------------- HELPERS ---------------- */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9]{10}$/;
const PINCODE_REGEX = /^[0-9]{4,10}$/;
const CONTACT_NAME_REGEX = /^[A-Za-z ]{2,50}$/;

const onlyDigits = (v) => String(v ?? "").replace(/\D+/g, "");
const isValidContactName = (value) => CONTACT_NAME_REGEX.test(String(value ?? "").trim());

const toPhoneNumber = (code, phone10) => {
    const digits = `${onlyDigits(code)}${onlyDigits(phone10)}`;
    return digits ? Number(digits) : undefined;
};

const splitTitleAndName = (fullName = "") => {
    const titles = ["Mr", "Mrs", "Ms", "Dr"];
    if (!fullName) return { title: "Mr", name: "" };

    const parts = fullName.trim().split(" ");
    if (parts.length && titles.includes(parts[0])) {
        return { title: parts[0], name: parts.slice(1).join(" ") };
    }
    return { title: "Mr", name: fullName.trim() };
};

const splitPhone = (fullPhone) => {
    const safe = String(fullPhone ?? "").trim();
    if (!safe) return { code: "+91", number: "" };

    const digits = onlyDigits(safe);

    const match = phoneCodes.find((p) => digits.startsWith(p.digits));
    if (match) {
        const number = digits.slice(match.digits.length).slice(-10);
        return { code: match.code, number };
    }

    return { code: "+91", number: digits.slice(-10) };
};

const getContactByType = (contactInfo = [], type) =>
    (contactInfo || []).find((c) => c?.type === type) || null;

const initialForm = {
    branchName: "",
    branchCode: "+91",
    branchPhone: "",

    address1: "",
    address2: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
    coords: {},
    fullAddress: "",

    primaryTitle: "Mr",
    primaryContact: "",
    primaryCode: "+91",
    primaryPhone: "",
    primaryEmail: "",

    secondaryTitle: "Mr",
    secondaryContact: "",
    secondaryCode: "+91",
    secondaryPhone: "",
    secondaryEmail: "",
};

const Branches = ({ branchData } = {}) => {
    const suggestionRef = useRef(null);

    /**
     * ✅ If branchData is provided -> VIEW ONLY mode
     * - Do NOT call getPartner()
     * - Do NOT call Ola key APIs (not needed, modal is hidden)
     */
    const isReadOnly = branchData !== undefined; // handles [] also as read-only

    const [partnerId, setPartnerId] = useState(null);

    const [pageLoading, setPageLoading] = useState(false);
    const [branches, setBranches] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const [expandedID, setExpandedID] = useState(null);

    const [editID, setEditID] = useState(null);
    const [deleteID, setDeleteID] = useState(null);

    const [formData, setFormData] = useState(initialForm);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [addressLoading, setAddressLoading] = useState(false);
    const [isAddressSelected, setIsAddressSelected] = useState(false);

    /* ---------------- INITIAL LOAD ---------------- */
    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            // ✅ VIEW ONLY: just set branches and exit
            if (isReadOnly) {
                if (!cancelled) {
                    setPartnerId(null);
                    setBranches(Array.isArray(branchData) ? branchData : []);
                    setPageLoading(false);
                }
                return;
            }

            // ✅ EDIT MODE: fetch partner branches + ola key
            setPageLoading(true);
            try {
                const resp = await getPartner();
                if (cancelled) return;

                if (!resp?.error && resp?.data) {
                    setPartnerId(resp.data._id);
                    setBranches(Array.isArray(resp?.data?.branches) ? resp.data.branches : []);
                } else {
                    showToast(resp?.message || "Failed to load branches", "Error");
                    setPartnerId(null);
                    setBranches([]);
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) {
                    showToast("Failed to load branches", "Error");
                    setPartnerId(null);
                    setBranches([]);
                }
            } finally {
                if (!cancelled) setPageLoading(false);
            }

        };

        init();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branchData, isReadOnly]);

    /* ---------------- CLOSE AUTOCOMPLETE ON OUTSIDE CLICK ---------------- */
    useEffect(() => {
        if (isReadOnly) return; // no modal, no suggestions
        const handleClickOutside = (e) => {
            if (suggestionRef.current && !suggestionRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isReadOnly]);

    /* ---------------- OPEN/CLOSE MODAL ---------------- */
    const openAddModal = () => {
        if (isReadOnly) return;
        setEditID(null);
        setFormData(initialForm);
        setSuggestions([]);
        setShowSuggestions(false);
        setIsAddressSelected(false);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditID(null);
        setFormData(initialForm);
        setSuggestions([]);
        setShowSuggestions(false);
        setIsAddressSelected(false);
    };

    /* ---------------- ADDRESS SEARCH ---------------- */
    const onAddressChange = async (e) => {
        if (isReadOnly) return;
        const query = e.target.value;

        setFormData((prev) => ({
            ...prev,
            address1: query,
            fullAddress: query,
            city: "",
            state: "",
            pincode: "",
            country: "India",
            coords: {},
        }));
        setIsAddressSelected(false);

        if (!query || query.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setAddressLoading(true);
        try {
            const results = await searchPlaces(query);
            setSuggestions(results || []);
            setShowSuggestions(true);
        } catch (err) {
            console.error(err);
            setSuggestions([]);
            setShowSuggestions(false);
            showToast("Failed to search addresses", "Error");
        } finally {
            setAddressLoading(false);
        }
    };

    const handleSelectSuggestion = async (item) => {
        if (isReadOnly) return;
        setShowSuggestions(false);

        try {
            const details = await getPlaceDetails(item.place_id);
            if (!details) return;

            let city = "";
            let state = "";
            let postalCode = "";
            let country = "";

            details.address_components?.forEach((c) => {
                if (c.types.includes("locality")) city = c.short_name;
                if (c.types.includes("administrative_area_level_1")) state = c.short_name;
                if (c.types.includes("postal_code")) postalCode = c.short_name;
                if (c.types.includes("country")) country = c.short_name;
            });

            const formatted = details.formatted_address || item.description;
            const shortAddress = details.name || item.description;

            setFormData((prev) => ({
                ...prev,
                address1: shortAddress,
                city,
                state,
                pincode: postalCode,
                country: country || prev.country,
                coords: details.geometry?.location || {},
                fullAddress: formatted,
            }));
            setIsAddressSelected(true);
        } catch (err) {
            console.error(err);
            showToast("Failed to get address details", "Error");
        }
    };

    /* ---------------- FORM CHANGE ---------------- */
    const handleChange = (e) => {
        if (isReadOnly) return;
        const { name, value } = e.target;

        if (name === "primaryPhone" || name === "secondaryPhone" || name === "branchPhone") {
            const digitsOnly = value.replace(/\D/g, "");
            setFormData((prev) => ({ ...prev, [name]: digitsOnly.slice(0, 10) }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    /* ---------------- VALIDATION ---------------- */
    const validateForm = () => {
        if (!formData.branchName.trim()) return (showToast("Branch name is required", "Error"), false);

        const branchPhone = formData.branchPhone.replace(/\s+/g, "");
        if (!branchPhone) return (showToast("Branch contact number is required", "Error"), false);
        if (!PHONE_REGEX.test(branchPhone))
            return (showToast("Enter a valid 10 digit branch contact number", "Error"), false);

        if (!formData.address1.trim() && !formData.fullAddress.trim())
            return (showToast("Address is required", "Error"), false);
        if (!isAddressSelected)
            return (showToast("Please select an address from suggestions", "Error"), false);

        if (!formData.city.trim()) return (showToast("City is required", "Error"), false);
        if (!formData.state.trim()) return (showToast("State is required", "Error"), false);
        if (!formData.country.trim()) return (showToast("Country is required", "Error"), false);

        const pin = String(formData.pincode ?? "").trim();
        if (!pin) return (showToast("Pincode is required", "Error"), false);
        if (!PINCODE_REGEX.test(pin)) return (showToast("Enter a valid pincode", "Error"), false);

        if (!formData.primaryContact.trim())
            return (showToast("Primary contact name is required", "Error"), false);
        if (!isValidContactName(formData.primaryContact))
            return (showToast("Enter a valid primary contact name", "Error"), false);

        const primaryPhone = formData.primaryPhone.replace(/\s+/g, "");
        if (!primaryPhone) return (showToast("Primary phone is required", "Error"), false);
        if (!PHONE_REGEX.test(primaryPhone))
            return (showToast("Enter a valid 10 digit primary phone number", "Error"), false);

        const primaryEmail = formData.primaryEmail.trim();
        if (!primaryEmail) return (showToast("Primary email is required", "Error"), false);
        if (!EMAIL_REGEX.test(primaryEmail))
            return (showToast("Enter a valid primary email", "Error"), false);

        const hasSecondary =
            !!formData.secondaryContact.trim() ||
            !!formData.secondaryPhone.trim() ||
            !!formData.secondaryEmail.trim();

        if (hasSecondary) {
            if (!formData.secondaryContact.trim())
                return (showToast("Secondary contact name is required", "Error"), false);
            if (!isValidContactName(formData.secondaryContact))
                return (showToast("Enter a valid secondary contact name", "Error"), false);

            const secondaryPhone = formData.secondaryPhone.replace(/\s+/g, "");
            if (!secondaryPhone) return (showToast("Secondary phone is required", "Error"), false);
            if (!PHONE_REGEX.test(secondaryPhone))
                return (showToast("Enter a valid 10 digit secondary phone number", "Error"), false);

            const secondaryEmail = formData.secondaryEmail.trim();
            if (!secondaryEmail) return (showToast("Secondary email is required", "Error"), false);
            if (!EMAIL_REGEX.test(secondaryEmail))
                return (showToast("Enter a valid secondary email", "Error"), false);
        }

        return true;
    };

    /* ---------------- ADD / UPDATE ---------------- */
    const refetchPartnerBranches = async () => {
        // helper used after add/update/delete
        try {
            setPageLoading(true);
            const resp = await getPartner();
            if (!resp?.error && resp?.data) {
                setPartnerId(resp.data._id);
                setBranches(Array.isArray(resp?.data?.branches) ? resp.data.branches : []);
            } else {
                showToast(resp?.message || "Failed to load branches", "Error");
                setBranches([]);
            }
        } catch (err) {
            console.error(err);
            showToast("Failed to load branches", "Error");
            setBranches([]);
        } finally {
            setPageLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (isReadOnly) return;

        if (!partnerId) {
            showToast("Partner information not loaded. Please try again.", "Error");
            return;
        }
        if (!validateForm()) return;

        const hasSecondary =
            !!formData.secondaryContact.trim() ||
            !!formData.secondaryPhone.trim() ||
            !!formData.secondaryEmail.trim();

        const payload = {
            partnerID: partnerId,
            name: formData.branchName.trim(),
            phone: toPhoneNumber(formData.branchCode, formData.branchPhone),
            addressInfo: {
                address1: formData.address1.trim(),
                address2: (formData.address2 || "").trim(),
                city: formData.city.trim(),
                state: formData.state.trim(),
                country: formData.country.trim(),
                pincode: Number(formData.pincode),
                coords: formData.coords,
                serviceRadius: 15,
                fullAddress: (formData.fullAddress || formData.address1).trim(),
            },
            contactInfo: [
                {
                    name: `${formData.primaryTitle} ${formData.primaryContact}`.trim(),
                    type: "primary",
                    phone: toPhoneNumber(formData.primaryCode, formData.primaryPhone),
                    email: formData.primaryEmail.trim(),
                },
                ...(hasSecondary
                    ? [
                        {
                            name: `${formData.secondaryTitle} ${formData.secondaryContact}`.trim(),
                            type: "secondary",
                            phone: toPhoneNumber(formData.secondaryCode, formData.secondaryPhone),
                            email: formData.secondaryEmail.trim(),
                        },
                    ]
                    : []),
            ],
        };

        try {
            setSaving(true);
            const res = editID ? await updateBranch({ ...payload, _id: editID }) : await addBranch(payload);

            if (res?.error) {
                showToast(res?.message || "Failed to save branch", "Error");
                return;
            }

            showToast(editID ? "Branch updated successfully" : "Branch added successfully", "Success");
            await refetchPartnerBranches();
            closeModal();
        } catch (err) {
            console.error(err);
            showToast("Failed to save branch. Please try again.", "Error");
        } finally {
            setSaving(false);
        }
    };

    /* ---------------- EDIT ---------------- */
    const handleEdit = (branch) => {
        if (isReadOnly) return;

        setEditID(branch._id);

        const primary = getContactByType(branch.contactInfo, "primary") || branch.contactInfo?.[0] || {};
        const secondary = getContactByType(branch.contactInfo, "secondary") || branch.contactInfo?.[1] || {};

        const { title: primaryTitle, name: primaryContact } = splitTitleAndName(primary.name || "");
        const { title: secondaryTitle, name: secondaryContact } = splitTitleAndName(secondary.name || "");

        const branchPhoneSplit = splitPhone(branch.phone || "");
        const primaryPhoneSplit = splitPhone(primary.phone || "");
        const secondaryPhoneSplit = splitPhone(secondary.phone || "");

        setFormData({
            ...initialForm,
            branchName: branch.name || "",
            branchCode: branchPhoneSplit.code,
            branchPhone: branchPhoneSplit.number,

            address1: branch.addressInfo?.address1 || "",
            address2: branch.addressInfo?.address2 || "",
            fullAddress: branch.addressInfo?.fullAddress || "",
            city: branch.addressInfo?.city || "",
            state: branch.addressInfo?.state || "",
            country: branch.addressInfo?.country || "India",
            pincode: branch.addressInfo?.pincode ? String(branch.addressInfo.pincode) : "",
            coords: branch.addressInfo?.coords || {},

            primaryTitle,
            primaryContact,
            primaryCode: primaryPhoneSplit.code,
            primaryPhone: primaryPhoneSplit.number,
            primaryEmail: primary.email || "",

            secondaryTitle,
            secondaryContact,
            secondaryCode: secondaryPhoneSplit.code,
            secondaryPhone: secondaryPhoneSplit.number,
            secondaryEmail: secondary.email || "",
        });

        setSuggestions([]);
        setShowSuggestions(false);
        setIsAddressSelected(true);
        setModalOpen(true);
    };

    /* ---------------- DELETE ---------------- */
    const confirmDelete = async () => {
        if (isReadOnly) return;
        if (!partnerId || !deleteID) return;

        try {
            const res = await deleteBranch(partnerId, deleteID);
            if (res?.error === false) {
                showToast(res.message || "Branch deleted successfully", "Success");
                await refetchPartnerBranches();
                setDeleteID(null);
            } else {
                showToast(res?.message || "Failed to delete branch", "Error");
            }
        } catch (err) {
            console.error(err);
            showToast("Failed to delete branch", "Error");
        }
    };

    const rows = useMemo(() => (Array.isArray(branches) ? branches : []), [branches]);

    const showPhone = (v) => {
        if (!v) return "-";
        try {
            return formatPhoneNumber(String(v));
        } catch {
            return String(v);
        }
    };

    return (
        <div className="font-poppins p-2 relative">
            {pageLoading && (
                <div className="absolute inset-0 z-[5] bg-white/60 flex items-center justify-center">
                    <Loader loading />
                </div>
            )}

            <div className="rounded-[18px] overflow-hidden border bg-white h-[89vh]">
                {/* HEADER */}
                <div className="bg-[#3E57E8] text-white px-4 sm:px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MdOutlineLocationOn size={24} />
                        <h2 className="text-sm sm:text-base font-semibold">Organization & Branch Setup</h2>
                    </div>

                    {/* Hide Add Branch in read-only mode */}
                    {!isReadOnly && (
                        <button
                            onClick={openAddModal}
                            className="bg-white text-[#3E57E8] text-xs sm:text-sm font-semibold px-3 sm:px-4 py-1.5 rounded-md
              border border-white hover:opacity-95 active:scale-[0.99] transition flex items-center gap-1"
                        >
                            <Plus size={16} />
                            Add Branch
                        </button>
                    )}
                </div>

                {/* TABLE */}
                <div className="p-3 sm:p-4">
                    <div className="rounded-xl border border-[#D6DCFF] bg-white overflow-hidden">
                        <div className="overflow-x-auto">
                            <div className="min-w-[980px]">
                                {/* header */}
                                <div className="grid grid-cols-12 gap-2 px-6 py-3 bg-[#F6F7FB] text-[12px] font-semibold text-[#6B7280]">
                                    <div className="col-span-2">Branch Name</div>
                                    <div className="col-span-4">Address</div>
                                    <div className="col-span-1">City</div>
                                    <div className="col-span-2">State</div>
                                    <div className="col-span-2 text-center">Contact No.</div>
                                    <div className="col-span-1 text-center">{isReadOnly ? "View" : "Actions"}</div>
                                </div>

                                {rows.length === 0 ? (
                                    <div className="px-4 py-10 text-center text-sm text-gray-500">No Branches Available.</div>
                                ) : (
                                    rows.map((branch) => {
                                        const isOpen = expandedID === branch._id;

                                        const primary =
                                            getContactByType(branch.contactInfo, "primary") || branch.contactInfo?.[0];
                                        const secondary =
                                            getContactByType(branch.contactInfo, "secondary") || branch.contactInfo?.[1];

                                        const stateText = [
                                            branch.addressInfo?.state,
                                            branch.addressInfo?.pincode ? String(branch.addressInfo?.pincode) : null,
                                        ]
                                            .filter(Boolean)
                                            .join(" - ");

                                        const selectedText = isOpen ? "text-[#3E57E8]" : "text-[#111827]";
                                        const rowBg = isOpen ? "bg-[#F5F7FF]" : "bg-white";

                                        return (
                                            <div key={branch._id} className="border-t border-[#EEF1FF]">
                                                {/* main row */}
                                                <div className={`grid grid-cols-12 gap-2 px-6 py-3 text-[12px] items-center ${rowBg}`}>
                                                    <div className={`col-span-2 font-semibold ${selectedText}`}>{branch.name || "-"}</div>

                                                    <div
                                                        className={`col-span-4 truncate ${isOpen ? "text-[#3E57E8]" : "text-gray-600"
                                                            }`}
                                                    >
                                                        {branch.addressInfo?.address1 || branch.addressInfo?.fullAddress || "-"}
                                                    </div>

                                                    <div className={`col-span-1 ${isOpen ? "text-[#3E57E8]" : "text-gray-600"}`}>
                                                        {branch.addressInfo?.city || "-"}
                                                    </div>

                                                    <div className={`col-span-2 ${isOpen ? "text-[#3E57E8]" : "text-gray-600"}`}>
                                                        {stateText || "-"}
                                                    </div>

                                                    <div
                                                        className={`col-span-2 text-center ${isOpen ? "text-[#3E57E8]" : "text-gray-600 min-w-[200px]"
                                                            }`}
                                                    >
                                                        {branch.phone ? showPhone(branch.phone) : "-"}
                                                    </div>

                                                    {/* actions */}
                                                    <div className="col-span-1 flex items-center justify-center gap-4">
                                                        {!isReadOnly && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleEdit(branch)}
                                                                    className="text-[#3E57E8] hover:opacity-80 transition"
                                                                    title="Edit"
                                                                >
                                                                    <Pencil size={14} />
                                                                </button>

                                                                <button
                                                                    onClick={() => setDeleteID(branch._id)}
                                                                    className="text-[#3E57E8] hover:opacity-80 transition"
                                                                    title="Delete"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </>
                                                        )}

                                                        {/* expand always available */}
                                                        <button
                                                            onClick={() => setExpandedID(isOpen ? null : branch._id)}
                                                            title={isOpen ? "Collapse" : "Expand"}
                                                            className={[
                                                                "h-7 w-7 inline-flex items-center justify-center rounded-full border transition",
                                                                isOpen
                                                                    ? "bg-[#3E57E8] border-[#3E57E8] text-white"
                                                                    : "bg-white border-[#D6DCFF] text-[#3E57E8] hover:bg-indigo-50",
                                                            ].join(" ")}
                                                        >
                                                            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* expanded contacts */}
                                                {isOpen && (
                                                    <div className="px-6 pb-4 bg-[#F5F7FF]">
                                                        <div className="rounded-2xl border border-[#D6DCFF] bg-white p-3 space-y-2">
                                                            {/* Primary */}
                                                            <div className="grid grid-cols-12 gap-2 items-center px-4 py-2 rounded-xl bg-[#F6F7FB] border border-[#EEF1FF] text-[12px]">
                                                                <div className="col-span-2 font-semibold text-gray-700">Primary Contact</div>
                                                                <div className="col-span-4 text-gray-700">{primary?.name || "-"}</div>
                                                                <div className="col-span-3 text-gray-700">
                                                                    {primary?.phone ? showPhone(primary.phone) : "-"}
                                                                </div>
                                                                <div className="col-span-3 text-gray-700 truncate">{primary?.email || "-"}</div>
                                                            </div>

                                                            {/* Secondary */}
                                                            <div className="grid grid-cols-12 gap-2 items-center px-4 py-2 rounded-xl bg-[#F6F7FB] border border-[#EEF1FF] text-[12px]">
                                                                <div className="col-span-2 font-semibold text-gray-700">Secondary Contact</div>
                                                                <div className="col-span-4 text-gray-700">{secondary?.name || "-"}</div>
                                                                <div className="col-span-3 text-gray-700">
                                                                    {secondary?.phone ? showPhone(secondary.phone) : "-"}
                                                                </div>
                                                                <div className="col-span-3 text-gray-700 truncate">{secondary?.email || "-"}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* MODAL (only when not read-only) */}
            {!isReadOnly && modalOpen && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
                    <div className="bg-white w-[94vw] max-w-6xl rounded-2xl shadow-xl border overflow-hidden">
                        {/* header */}
                        <div className="px-6 py-4 flex items-center justify-between border-b">
                            <div className="flex items-center gap-2 text-[#3E57E8]">
                                <MapPin size={18} />
                                <h3 className="font-semibold text-[15px]">{editID ? "Update Branch" : "Add Branch"}</h3>
                            </div>

                            <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100" title="Close">
                                <X size={18} />
                            </button>
                        </div>

                        {/* body */}
                        <div className="px-6 py-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <Field
                                    label="Branch Name"
                                    required
                                    name="branchName"
                                    value={formData.branchName}
                                    onChange={handleChange}
                                    placeholder="Enter Branch"
                                />

                                <PhoneWithCode
                                    label="Branch Contact Number"
                                    prefix="branch"
                                    formData={formData}
                                    onChange={handleChange}
                                    required
                                />

                                {/* Address autocomplete */}
                                <div ref={suggestionRef} className="relative">
                                    <label className="block text-[13px] font-medium text-gray-700">
                                        Address <span className="text-red-500">*</span>
                                    </label>

                                    <input
                                        type="text"
                                        value={formData.fullAddress || formData.address1}
                                        onChange={onAddressChange}
                                        className={inputClass}
                                    />

                                    {showSuggestions && (
                                        <div className="absolute z-[9999] bg-white border w-full mt-1 rounded-xl shadow max-h-56 overflow-y-auto">
                                            {addressLoading ? (
                                                <div className="flex items-center justify-center p-3">
                                                    <Loader loading />
                                                </div>
                                            ) : suggestions.length > 0 ? (
                                                suggestions.map((item, i) => (
                                                    <button
                                                        type="button"
                                                        key={i}
                                                        onClick={() => handleSelectSuggestion(item)}
                                                        className="w-full text-left p-3 text-[12px] hover:bg-indigo-50 border-b last:border-none"
                                                    >
                                                        {item.description}
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="p-3 text-[12px] text-gray-500">No addresses found</div>
                                            )}
                                        </div>
                                    )}

                                    {(formData.fullAddress || formData.address1) && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        address1: "",
                                                        address2: "",
                                                        fullAddress: "",
                                                        city: "",
                                                        state: "",
                                                        pincode: "",
                                                        country: "India",
                                                        coords: {},
                                                    }));
                                                    setIsAddressSelected(false);
                                                }
                                            }
                                            className="mt-2 text-[12px] text-gray-700 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50"
                                        >
                                            Clear address
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* CONTACTS BOX */}
                            <div className="mt-6 rounded-2xl border border-[#D6DCFF] p-5">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <NameWithTitle
                                        prefix="primary"
                                        label="Primary Contact Person Name"
                                        formData={formData}
                                        onChange={handleChange}
                                        required
                                    />
                                    <PhoneWithCode
                                        prefix="primary"
                                        label="Contact Number"
                                        formData={formData}
                                        onChange={handleChange}
                                        required
                                    />
                                    <Field
                                        label="Email ID"
                                        required
                                        name="primaryEmail"
                                        value={formData.primaryEmail}
                                        onChange={handleChange}
                                        placeholder=""
                                    />
                                </div>

                                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <NameWithTitle
                                        prefix="secondary"
                                        label="Secondary Contact Person Name"
                                        formData={formData}
                                        onChange={handleChange}
                                        required={false}
                                    />
                                    <PhoneWithCode
                                        prefix="secondary"
                                        label="Contact Number"
                                        formData={formData}
                                        onChange={handleChange}
                                        required={false}
                                    />
                                    <Field
                                        label="Email ID"
                                        required={false}
                                        name="secondaryEmail"
                                        value={formData.secondaryEmail}
                                        onChange={handleChange}
                                        placeholder=""
                                    />
                                </div>
                            </div>

                            {/* Save */}
                            <div className="mt-8 flex justify-center">
                                <button
                                    onClick={handleSubmit}
                                    disabled={saving}
                                    className={`px-10 py-2 rounded-lg bg-[#3E57E8] text-white text-sm font-semibold
                  hover:bg-[#3249d6] transition ${saving ? "opacity-70 cursor-not-allowed" : ""}`}
                                >
                                    {saving ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRM (only when not read-only) */}
            {!isReadOnly && deleteID && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3">
                    <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-sm space-y-4 border">
                        <h3 className="font-semibold text-sm">Confirm Delete</h3>
                        <p className="text-xs text-gray-600">Are you sure you want to delete this branch?</p>

                        <div className="flex justify-end gap-2">
                            <button onClick={() => setDeleteID(null)} className="px-3 py-2 text-xs border rounded-lg hover:bg-gray-50">
                                Cancel
                            </button>
                            <button onClick={confirmDelete} className="px-3 py-2 text-xs bg-red-600 text-white rounded-lg hover:opacity-95">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Branches;

/* ---------------- UI REUSABLES ---------------- */

const inputClass =
    "mt-1 w-full bg-[#F6F7FB] border border-[#E6E9F5] rounded-xl px-3 py-2 text-[13px] " +
    "focus:outline-none focus:ring-2 focus:ring-indigo-500";

const Field = ({ label, required, name, value, onChange, placeholder }) => (
    <div className="flex flex-col">
        <label className="text-[13px] font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input name={name} value={value} onChange={onChange} placeholder={placeholder} className={inputClass} />
    </div>
);

const NameWithTitle = ({ prefix, formData, onChange, required, label = "Name" }) => (
    <div className="flex flex-col">
        <label className="text-[13px] font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
        </label>

        <div className="mt-1 flex bg-[#F6F7FB] border border-[#E6E9F5] rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500">
            <select
                name={`${prefix}Title`}
                value={formData[`${prefix}Title`]}
                onChange={onChange}
                className="px-3 py-2 bg-transparent border-r border-[#E6E9F5] text-[13px] outline-none"
            >
                <option>Mr</option>
                <option>Mrs</option>
                <option>Ms</option>
                <option>Dr</option>
            </select>

            <input
                name={`${prefix}Contact`}
                value={formData[`${prefix}Contact`]}
                onChange={onChange}
                placeholder="full name"
                className="w-full px-3 py-2 text-[13px] bg-transparent outline-none"
            />
        </div>
    </div>
);

const PhoneWithCode = ({ prefix, formData, onChange, required, label = "Phone" }) => (
    <div className="flex flex-col">
        <label className="text-[13px] font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
        </label>

        <div className="mt-1 flex bg-[#F6F7FB] border border-[#E6E9F5] rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500">
            <select
                name={`${prefix}Code`}
                value={formData[`${prefix}Code`]}
                onChange={onChange}
                className="px-3 py-2 bg-transparent border-r border-[#E6E9F5] text-[13px] outline-none"
            >
                {phoneCodes.map((item) => (
                    <option key={item.code} value={item.code}>
                        {item.label}
                    </option>
                ))}
            </select>

            <input
                name={`${prefix}Phone`}
                value={formData[`${prefix}Phone`]}
                onChange={onChange}
                className="w-full px-3 py-2 text-[13px] bg-transparent outline-none"
                maxLength={10}
            />
        </div>
    </div>
);
